import React, { useState } from 'react'
import { X, Download, FileText, Calendar, Filter } from 'lucide-react'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'

interface DriftAuditExportModalProps {
  isOpen: boolean
  onClose: () => void
  tenantId: string
  tenantName?: string
}

const EVENT_TYPES = [
  { value: 'Approved', label: 'Approved', description: 'User approved drift' },
  { value: 'Rejected', label: 'Rejected', description: 'User rejected drift' },
  { value: 'Remediated', label: 'Remediated', description: 'Drift was fixed' },
  { value: 'Redetected', label: 'Redetected', description: 'Drift reappeared' },
  { value: 'AutoApproved', label: 'Auto-Approved', description: 'System auto-approved' },
]

export default function DriftAuditExportModal({
  isOpen,
  onClose,
  tenantId,
  tenantName
}: DriftAuditExportModalProps) {
  const [format, setFormat] = useState<'csv' | 'html'>('csv')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([])
  const [isExporting, setIsExporting] = useState(false)

  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isExporting) {
      onClose()
    }
  }

  const handleEventTypeToggle = (eventType: string) => {
    setSelectedEventTypes(prev =>
      prev.includes(eventType)
        ? prev.filter(t => t !== eventType)
        : [...prev, eventType]
    )
  }

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const blob = await api.downloadDriftAuditReport(
        tenantId,
        format,
        startDate || undefined,
        endDate || undefined,
        selectedEventTypes.length > 0 ? selectedEventTypes : undefined
      )

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const timestamp = new Date().toISOString().split('T')[0]
      link.download = `drift-audit-${tenantName || tenantId}-${timestamp}.${format}`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success(`Drift audit report downloaded successfully`)
      onClose()
    } catch (error: any) {
      console.error('Export failed:', error)

      // Try to parse error message from blob if it's JSON
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text()
          const errorData = JSON.parse(text)
          toast.error(errorData.message || 'Failed to export audit report')
        } catch {
          toast.error('Failed to export audit report')
        }
      } else {
        toast.error(error?.response?.data?.message || error?.message || 'Failed to export audit report')
      }
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-dark-800 rounded-lg shadow-dark-card border border-dark-700 max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-700">
          <div className="flex items-center">
            <FileText className="h-5 w-5 text-purple-400 mr-3" />
            <h3 className="text-lg font-medium text-slate-100">
              Export Drift Audit History
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={isExporting}
            className="text-slate-500 hover:text-slate-300 transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Format Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-3">
              Export Format
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setFormat('csv')}
                className={`flex-1 px-4 py-3 rounded-lg border transition-all ${
                  format === 'csv'
                    ? 'border-purple-500 bg-purple-500/20 text-purple-300'
                    : 'border-dark-600 bg-dark-700 text-slate-400 hover:border-dark-500'
                }`}
              >
                <div className="font-medium">CSV</div>
                <div className="text-xs mt-1 opacity-75">For Excel / Data Analysis</div>
              </button>
              <button
                type="button"
                onClick={() => setFormat('html')}
                className={`flex-1 px-4 py-3 rounded-lg border transition-all ${
                  format === 'html'
                    ? 'border-purple-500 bg-purple-500/20 text-purple-300'
                    : 'border-dark-600 bg-dark-700 text-slate-400 hover:border-dark-500'
                }`}
              >
                <div className="font-medium">HTML</div>
                <div className="text-xs mt-1 opacity-75">Styled Report / Printing</div>
              </button>
            </div>
          </div>

          {/* Date Range */}
          <div>
            <label className="flex items-center text-sm font-medium text-slate-300 mb-3">
              <Calendar className="h-4 w-4 mr-2 text-slate-500" />
              Date Range <span className="text-slate-500 font-normal ml-2">(optional)</span>
            </label>
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-xs text-slate-500 mb-1">From</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-slate-500 mb-1">To</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500"
                />
              </div>
            </div>
          </div>

          {/* Event Type Filter */}
          <div>
            <label className="flex items-center text-sm font-medium text-slate-300 mb-3">
              <Filter className="h-4 w-4 mr-2 text-slate-500" />
              Event Types <span className="text-slate-500 font-normal ml-2">(optional - all if none selected)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {EVENT_TYPES.map((eventType) => (
                <button
                  key={eventType.value}
                  type="button"
                  onClick={() => handleEventTypeToggle(eventType.value)}
                  title={eventType.description}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    selectedEventTypes.includes(eventType.value)
                      ? 'bg-purple-500/30 text-purple-300 border border-purple-500/50'
                      : 'bg-dark-700 text-slate-400 border border-dark-600 hover:border-dark-500'
                  }`}
                >
                  {eventType.label}
                </button>
              ))}
            </div>
          </div>

          {/* Info box */}
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
            <p className="text-sm text-purple-300">
              The audit history includes a complete timeline for each drift: who approved or rejected it, when, their role, and any comments provided. This is useful for compliance documentation and accountability tracking.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-dark-900 rounded-b-lg flex justify-end gap-3 border-t border-dark-700">
          <button
            onClick={onClose}
            disabled={isExporting}
            className="px-4 py-2 text-slate-400 hover:text-slate-300 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-dark-800 transition-colors disabled:opacity-50"
          >
            {isExporting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Export
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
